import { create } from 'zustand'
import type { CompositeResult } from '@/core/composite'
import type { LayoutConfig } from '@/core/types'
import { newSessionKey } from '@/core/session'

/**
 * State for one guest's trip through the console.
 *
 * The session key is minted here, at the moment the operator commits to a
 * selection, rather than by the agent when the folder is created. That ordering
 * matters: the QR is derived from the key alone, so the composite rendered in
 * step 2 already carries the correct code — and the folder is only created when
 * the operator actually prints, so backing out of a step leaves nothing behind.
 */

export interface RenderedLayout {
  layout: LayoutConfig
  result: CompositeResult
  previewUrl: string
}

export interface FlowState {
  step: number
  /** Identifiers of selected photos, in the order they were tapped. */
  selected: string[]
  /** Minted when leaving step 1; names the folder and derives the QR. */
  sessionKey: string | null

  rendered: RenderedLayout[] | null
  renderError: string | null
  chosenLayoutId: string | null
  copies: number

  printing: boolean

  setStep: (step: number) => void
  toggleSelect: (identifier: string) => void
  clearSelection: () => void

  beginSession: () => string
  setRendered: (rendered: RenderedLayout[] | null) => void
  setRenderError: (message: string | null) => void
  chooseLayout: (layoutId: string) => void
  setCopies: (copies: number) => void
  setPrinting: (printing: boolean) => void

  /** Releases blob URLs and returns to an empty step 1, ready for the next guest. */
  reset: () => void
}

export const useFlowStore = create<FlowState>((set, get) => ({
  step: 0,
  selected: [],
  sessionKey: null,
  rendered: null,
  renderError: null,
  chosenLayoutId: null,
  copies: 1,
  printing: false,

  setStep: (step) => set({ step }),

  toggleSelect: (identifier) =>
    set((state) => ({
      selected: state.selected.includes(identifier)
        ? state.selected.filter((id) => id !== identifier)
        : [...state.selected, identifier],
    })),

  clearSelection: () => set({ selected: [] }),

  beginSession: () => {
    const existing = get().sessionKey
    if (existing) return existing
    const key = newSessionKey()
    set({ sessionKey: key })
    return key
  },

  setRendered: (rendered) => set({ rendered }),
  setRenderError: (renderError) => set({ renderError }),
  chooseLayout: (chosenLayoutId) => set({ chosenLayoutId }),
  setCopies: (copies) => set({ copies: Math.max(1, Math.min(99, Math.trunc(copies) || 1)) }),
  setPrinting: (printing) => set({ printing }),

  reset: () => {
    // Composites are full-resolution PNGs held as object URLs; leaking one per
    // guest would grow the tab's memory all evening.
    for (const item of get().rendered ?? []) URL.revokeObjectURL(item.previewUrl)
    set({
      step: 0,
      selected: [],
      sessionKey: null,
      rendered: null,
      renderError: null,
      chosenLayoutId: null,
      copies: 1,
      printing: false,
    })
  },
}))
